<div class="flex flex-col gap-8 w-full">
  <!-- Section 1: Briefing Notes -->
  @if (!campaign() || briefingNotesData().briefingNote) {
    <div class="flex flex-col gap-2">
      @if (!campaign()) {
        <ng-container *ngTemplateOutlet="titleDescriptionSkeletonLoader" />
      } @else {
        <div class="flex flex-col gap-1">
          <h2 class="text-base font-bold">{{ 'pages.campaignDetails.creatorOverview.briefing' | translate }}</h2>
          <p class="text-xs max-md:text-[13px] text-shikenso-purple-base-60">
            {{ 'pages.campaignDetails.creatorOverview.briefingDescription' | translate }}
          </p>
        </div>
      }

      <!-- Mobile: smaller rich-text headings so long words like "Kampagnenüberblick" fit on one line. -->
      <div
        class="rounded-2xl bg-shikenso-gray-base-24 px-6 py-3 max-md:px-4 max-md:pt-4 max-md:pb-2 max-md:[&_.ql-editor]:px-0 max-md:[&_.ql-editor_h1]:text-xl max-md:[&_.ql-editor_h2]:text-[17px] max-md:[&_.ql-editor_h3]:text-[15px]"
      >
        <app-briefing-notes class="w-full" [data]="briefingNotesData()" [loading]="!campaign()" />
      </div>
    </div>
  }

  <!-- Section 2: Campaign Overview -->
  <div class="flex flex-col gap-4">
    @let anyCardDataLoaded = analyticsNodes() || ctaKpiStatus() || !contentFormatsLoading();

    @if (anyCardDataLoaded) {
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-bold">{{ 'pages.campaignDetails.creatorOverview.campaignOverview' | translate }}</h2>
        <p class="text-xs max-md:text-[13px] text-shikenso-purple-base-60">
          {{ 'pages.campaignDetails.creatorOverview.campaignOverviewDescription' | translate }}
        </p>
      </div>
    } @else {
      <ng-container *ngTemplateOutlet="titleDescriptionSkeletonLoader" />
    }

    <!-- 4 Info Cards. Mobile: long single words (German "Kampagnenanforderungen") wrap inside the half-width card. -->
    <div class="grid grid-cols-4 gap-4 text-sm items-start max-md:grid-cols-2 max-md:gap-2 max-md:break-words">
      <!-- Card 1: Contents by platform -->
      <div class="rounded-xl border border-shikenso-purple-base-12 p-2 max-md:p-3 flex flex-col gap-3 min-h-[150px]">
        <ng-template #platformFeatureItem let-item let-loading="loading">
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-1 h-[22px] max-md:min-w-0">
              @if (loading) {
                <ngx-skeleton-loader class="h-4" [theme]="{ width: '16px', height: '16px', 'border-radius': '50%' }" />
                <ngx-skeleton-loader class="h-5" [theme]="{ width: '80px', height: '20px', 'border-radius': '4px' }" />
              } @else {
                <app-social-badge
                  class="shrink-0"
                  backgroundColor="transparent"
                  [type]="item.platformFeature"
                  [size]="16"
                  [iconSize]="16"
                  [color]="item.color"
                />
                <span class="max-md:truncate" [style.color]="item.color">
                  {{ 'shared.platformFeatures.' + item.platformFeature | translate }}
                </span>
              }
            </div>

            @if (loading) {
              <ngx-skeleton-loader class="h-5" [theme]="{ width: '20px', height: '20px', 'border-radius': '4px' }" />
            } @else {
              <span class="pr-3 max-md:pr-0 shrink-0">{{ item.count }}</span>
            }
          </div>
        </ng-template>

        @if (!analyticsNodes()) {
          <ngx-skeleton-loader class="h-5" [theme]="{ width: '120px', height: '20px', 'border-radius': '4px' }" />

          <div class="flex flex-col gap-1">
            @for (i of [1, 2, 3, 4]; track i) {
              <ng-container *ngTemplateOutlet="platformFeatureItem; context: { $implicit: null, loading: true }" />
            }
          </div>
        } @else {
          <span class="font-bold">
            {{ platformFeatureGroups().total }}
            {{ 'pages.campaignDetails.creatorOverview.contents' | translate }}
          </span>

          <div class="flex flex-col gap-1 text-sm">
            @let items = platformFeatureGroups().items;

            @if (items.length <= 4) {
              @for (item of items; track item.platformFeature) {
                <ng-container *ngTemplateOutlet="platformFeatureItem; context: { $implicit: item, loading: false }" />
              }
            } @else {
              @for (item of items.slice(0, 3); track item.platformFeature) {
                <ng-container *ngTemplateOutlet="platformFeatureItem; context: { $implicit: item, loading: false }" />
              }

              <div
                class="grid transition-[grid-template-rows,margin-top] duration-200"
                [style.grid-template-rows]="platformFeatureCardExpanded() ? '1fr' : '0fr'"
                [style.margin-top]="platformFeatureCardExpanded() ? null : '-0.25rem'"
              >
                <div class="overflow-hidden">
                  <div class="flex flex-col gap-1">
                    @for (item of items.slice(3); track item.platformFeature) {
                      <ng-container
                        *ngTemplateOutlet="platformFeatureItem; context: { $implicit: item, loading: false }"
                      />
                    }
                  </div>
                </div>
              </div>

              <div class="flex">
                <button
                  mat-flat-button
                  type="button"
                  color="secondary-transparent"
                  class="mat-micro-button"
                  (click)="platformFeatureCardExpanded.set(!platformFeatureCardExpanded())"
                >
                  <div class="flex items-center gap-1">
                    <span>
                      {{
                        platformFeatureCardExpanded()
                          ? ('pages.campaignDetails.creatorOverview.showLess' | translate)
                          : ('pages.campaignDetails.creatorOverview.showMore' | translate: { count: items.length - 3 })
                      }}
                    </span>

                    @if (platformFeatureCardExpanded()) {
                      <app-icon src="icons/arrow-up.svg" [size]="12" />
                    } @else {
                      <app-icon src="icons/arrow-down.svg" [size]="12" />
                    }
                  </div>
                </button>
              </div>
            }
          </div>
        }
      </div>

      <!-- Card 2: Content Formats (CTA templates) -->
      <div class="rounded-xl border border-shikenso-purple-base-12 p-2 max-md:p-3 flex flex-col gap-3 min-h-[150px]">
        <ng-template #ctaTemplateItem let-item let-loading="loading">
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-1 h-[22px] max-md:min-w-0">
              @if (loading) {
                <ngx-skeleton-loader class="h-4" [theme]="{ width: '16px', height: '16px', 'border-radius': '50%' }" />
                <ngx-skeleton-loader class="h-5" [theme]="{ width: '80px', height: '20px', 'border-radius': '4px' }" />
              } @else {
                <app-social-badge
                  class="shrink-0"
                  backgroundColor="transparent"
                  [type]="item.platformFeature"
                  [size]="16"
                  [iconSize]="16"
                  [color]="item.color"
                />
                <span class="max-md:truncate">{{ item.name }}</span>
              }
            </div>
          </div>
        </ng-template>

        @if (contentFormatsLoading()) {
          <ngx-skeleton-loader class="h-5" [theme]="{ width: '120px', height: '20px', 'border-radius': '4px' }" />

          <div class="flex flex-col gap-1">
            @for (i of [1, 2, 3, 4]; track i) {
              <ng-container *ngTemplateOutlet="ctaTemplateItem; context: { $implicit: null, loading: true }" />
            }
          </div>
        } @else {
          <span class="font-bold">
            {{ ctaTemplateItems().total }}
            {{ 'pages.campaignDetails.creatorOverview.contentFormats' | translate }}
          </span>

          <div class="flex flex-col gap-1">
            @let items = ctaTemplateItems().items;

            @if (items.length <= 4) {
              @for (item of items; track item.name) {
                <ng-container *ngTemplateOutlet="ctaTemplateItem; context: { $implicit: item, loading: false }" />
              }
            } @else {
              @for (item of items.slice(0, 3); track item.name) {
                <ng-container *ngTemplateOutlet="ctaTemplateItem; context: { $implicit: item, loading: false }" />
              }

              <div
                class="grid transition-[grid-template-rows,margin-top] duration-200"
                [style.grid-template-rows]="ctaTemplateCardExpanded() ? '1fr' : '0fr'"
                [style.margin-top]="ctaTemplateCardExpanded() ? null : '-0.25rem'"
              >
                <div class="overflow-hidden">
                  <div class="flex flex-col gap-1">
                    @for (item of items.slice(3); track item.name) {
                      <ng-container *ngTemplateOutlet="ctaTemplateItem; context: { $implicit: item, loading: false }" />
                    }
                  </div>
                </div>
              </div>

              <div class="flex">
                <button
                  mat-flat-button
                  type="button"
                  color="secondary-transparent"
                  class="mat-micro-button"
                  (click)="ctaTemplateCardExpanded.set(!ctaTemplateCardExpanded())"
                >
                  <div class="flex items-center gap-1">
                    <span>
                      {{
                        ctaTemplateCardExpanded()
                          ? ('pages.campaignDetails.creatorOverview.showLess' | translate)
                          : ('pages.campaignDetails.creatorOverview.showMore' | translate: { count: items.length - 3 })
                      }}
                    </span>

                    @if (ctaTemplateCardExpanded()) {
                      <app-icon src="icons/arrow-up.svg" [size]="12" />
                    } @else {
                      <app-icon src="icons/arrow-down.svg" [size]="12" />
                    }
                  </div>
                </button>
              </div>
            }
          </div>
        }
      </div>

      <!-- Card 3: Campaign Requirements -->
      <div
        class="rounded-xl border border-shikenso-purple-base-12 p-2 max-md:p-3 flex flex-col gap-3 h-[150px] max-md:h-auto max-md:min-h-[96px]"
      >
        @if (!analyticsNodes()) {
          <ngx-skeleton-loader class="h-5" [theme]="{ width: '164px', height: '20px', 'border-radius': '4px' }" />
          <ngx-skeleton-loader class="h-5" [theme]="{ width: '100px', height: '20px', 'border-radius': '4px' }" />
        } @else {
          <span class="font-bold">
            {{ 'pages.campaignDetails.creatorOverview.campaignRequirements' | translate }}
          </span>

          <div class="flex flex-col gap-1">
            <span>{{ campaignRequirements().deliverables }} {{ 'shared.tile.deliverables' | translate }}</span>
          </div>
        }
      </div>

      <!-- Card 4: Progress -->
      <div
        class="rounded-xl border border-shikenso-purple-base-12 p-2 max-md:p-3 flex flex-col gap-3 h-[150px] max-md:h-auto max-md:min-h-[96px]"
      >
        @if (!ctaKpiStatus() && !analyticsNodes()) {
          <ngx-skeleton-loader class="h-5" [theme]="{ width: '164px', height: '20px', 'border-radius': '4px' }" />
          <ngx-skeleton-loader class="h-5" [theme]="{ width: '100px', height: '20px', 'border-radius': '4px' }" />
        } @else {
          <span class="font-bold">
            {{ 'pages.campaignDetails.creatorOverview.progress' | translate }}
          </span>

          <span>
            {{ progressData().current }} / {{ progressData().total }}
            {{ 'pages.campaignDetails.creatorOverview.contents' | translate }}
          </span>
        }
      </div>
    </div>

    <!-- View all planned contents button -->
    <div>
      @if (!analyticsNodes()) {
        <ngx-skeleton-loader class="h-7" [theme]="{ width: '174px', height: '28px', 'border-radius': '100vw' }" />
      } @else {
        <button
          mat-flat-button
          type="button"
          color="secondary-transparent"
          class="mat-tiny-plus-button !px-2"
          (click)="viewAllPlannedContentsClicked.emit()"
        >
          <div class="flex items-center gap-3">
            <span class="text-xs max-md:text-sm">
              {{ 'pages.campaignDetails.creatorOverview.viewAllPlannedContents' | translate }}
            </span>
            <div class="border border-shikenso-purple-base-12 rounded-full p-[5px] max-md:p-2">
              <app-icon src="icons/arrow-right.svg" [size]="12" />
            </div>
          </div>
        </button>
      }
    </div>
  </div>

  <!-- Section 3: Always Required -->
  @if (!analyticsNodes()) {
    <div class="flex flex-col gap-4">
      <ng-container *ngTemplateOutlet="tileSectionSkeletonLoader" />
    </div>
  } @else if (alwaysTiles().length > 0) {
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-bold">
          {{ 'pages.campaignDetails.creatorOverview.alwaysRequired' | translate }}
        </h2>
        <p class="text-xs max-md:text-[13px] text-shikenso-purple-base-60">
          {{ 'pages.campaignDetails.creatorOverview.alwaysRequiredDescription' | translate }}
        </p>
      </div>

      <div class="flex flex-col gap-3">
        @for (tile of alwaysTiles(); track tile.id) {
          <app-social-content-tile
            [tileConfiguration]="tile"
            [briefingOverview]="true"
            [creatorBriefingMode]="true"
            (tileClicked)="tileClicked.emit($event)"
          />
        }
      </div>
    </div>
  }

  <!-- Section 4: Next Up -->
  @if (!analyticsNodes()) {
    <div class="flex flex-col gap-4">
      <ng-container *ngTemplateOutlet="tileSectionSkeletonLoader" />
    </div>
  } @else if (nextUpGroups().length > 0) {
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-bold">{{ 'pages.campaignDetails.creatorOverview.nextUp' | translate }}</h2>
        <p class="text-xs max-md:text-[13px] text-shikenso-purple-base-60">
          {{ 'pages.campaignDetails.creatorOverview.nextUpDescription' | translate }}
        </p>
      </div>

      @for (group of nextUpGroups(); track group.label) {
        <div class="flex flex-col gap-3">
          <h3 class="text-sm font-bold text-shikenso-purple-base-60">{{ group.label }}</h3>
          @for (tile of group.tiles; track tile.id) {
            <app-social-content-tile
              [tileConfiguration]="tile"
              [briefingOverview]="true"
              [creatorBriefingMode]="true"
              (tileClicked)="tileClicked.emit($event)"
            />
          }
        </div>
      }
    </div>
  }

  <!-- Section 4b: Within a Time Frame -->
  @if (!analyticsNodes()) {
    <div class="flex flex-col gap-4">
      <ng-container *ngTemplateOutlet="tileSectionSkeletonLoader" />
    </div>
  } @else if (timeFrameTiles().length > 0) {
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-bold">
          {{ 'pages.campaignDetails.creatorOverview.timeFrameRequired' | translate }}
        </h2>
        <p class="text-xs max-md:text-[13px] text-shikenso-purple-base-60">
          {{ 'pages.campaignDetails.creatorOverview.timeFrameRequiredDescription' | translate }}
        </p>
      </div>
      <div class="flex flex-col gap-3">
        @for (tile of timeFrameTiles(); track tile.id) {
          <app-social-content-tile
            [tileConfiguration]="tile"
            [briefingOverview]="true"
            [creatorBriefingMode]="true"
            (tileClicked)="tileClicked.emit($event)"
          />
        }
      </div>
    </div>
  }

  <!-- Section 5: Upcoming Tasks -->
  @if (!analyticsNodes()) {
    <div class="flex flex-col gap-4">
      <ng-container *ngTemplateOutlet="tileSectionSkeletonLoader" />
    </div>
  } @else if (undesignatedTiles().length > 0) {
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-bold">
          {{ 'pages.campaignDetails.creatorOverview.upcomingTasks' | translate }}
        </h2>
        <p class="text-xs max-md:text-[13px] text-shikenso-purple-base-60">
          {{ 'pages.campaignDetails.creatorOverview.upcomingTasksDescription' | translate }}
        </p>
      </div>
      <div class="flex flex-col gap-3">
        @for (tile of undesignatedTiles(); track tile.id) {
          <app-social-content-tile
            [tileConfiguration]="tile"
            [briefingOverview]="true"
            [creatorBriefingMode]="true"
            (tileClicked)="tileClicked.emit($event)"
          />
        }
      </div>
    </div>
  }

  <!-- Section 6: Optional -->
  @if (!analyticsNodes()) {
    <div class="flex flex-col gap-4">
      <ng-container *ngTemplateOutlet="tileSectionSkeletonLoader" />
    </div>
  } @else if (optionalTiles().length > 0) {
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-bold">
          {{ 'pages.campaignDetails.creatorOverview.optional' | translate }}
        </h2>
        <p class="text-xs max-md:text-[13px] text-shikenso-purple-base-60">
          {{ 'pages.campaignDetails.creatorOverview.optionalDescription' | translate }}
        </p>
      </div>
      <div class="flex flex-col gap-3">
        @for (tile of optionalTiles(); track tile.id) {
          <app-social-content-tile
            [tileConfiguration]="tile"
            [briefingOverview]="true"
            [creatorBriefingMode]="true"
            (tileClicked)="tileClicked.emit($event)"
          />
        }
      </div>
    </div>
  }
</div>

<ng-template #titleDescriptionSkeletonLoader>
  <div class="flex flex-col gap-1">
    <ngx-skeleton-loader class="h-6" [theme]="{ width: '124px', height: '24px', 'border-radius': '4px' }" />
    <ngx-skeleton-loader class="h-4" [theme]="{ width: '304px', height: '16px', 'border-radius': '4px' }" />
  </div>
</ng-template>

<ng-template #tileSectionSkeletonLoader>
  <ng-container *ngTemplateOutlet="titleDescriptionSkeletonLoader" />

  <div class="flex flex-col gap-3">
    <ngx-skeleton-loader class="h-[139px]" [theme]="{ height: '139px', 'border-radius': '16px' }" />
    <ngx-skeleton-loader class="h-[139px]" [theme]="{ height: '139px', 'border-radius': '16px' }" />
  </div>
</ng-template>
